import { useLayoutEffect, useState, useCallback, useMemo, ChangeEvent } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { Search, SlidersHorizontal } from "lucide-react";

import {
	Badge,
	Button,
	DateRangePicker,
	Input,
	MultiSelect,
	SearchableSelect,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@hotelise/ui";
import type { DateRange } from "@hotelise/ui";

import {
	getReservations,
	getReservationsFilter,
	setReservationsFilter,
} from "state/modules/reservations";
import { getPropertiesList } from "state/modules/properties";

import {
	ComplexFilterType,
	ComplexFilterItem,
	SearchComplexFilter,
	DateComplexFilter,
	ReservationsStatusComplexFilter,
	ReservationsChannelComplexFilter,
	PropertyIdComplexFilter,
	DateFilterType,
	DateRangeFilter,
} from "interfaces/filters";

import { RESERVATION_STATUSES } from "interfaces/reservations";
import { CHANNEL_NAMES } from "interfaces/channels";

const STATUS_OPTIONS = [
	{ id: "all", label: "All" },
	{ id: RESERVATION_STATUSES.ACCEPTED, label: "Accepted" },
	{ id: RESERVATION_STATUSES.CANCELLED, label: "Cancelled" },
	{ id: RESERVATION_STATUSES.PENDING, label: "Pending" },
	{ id: RESERVATION_STATUSES.EXPIRED, label: "Expired" },
] as const;

const CHANNEL_OPTIONS = [
	{ id: "all", label: "All Channels" },
	{ id: CHANNEL_NAMES.BOOKING, label: "Booking" },
	{ id: CHANNEL_NAMES.AIRBNB, label: "Airbnb" },
	{ id: CHANNEL_NAMES.DIRECT, label: "Direct" },
	{ id: CHANNEL_NAMES.MANUAL, label: "Manual" },
	{ id: CHANNEL_NAMES.VRBO, label: "Vrbo" },
];

const DATE_PRESETS = [
	{
		label: "Last 30 days",
		getRange: () => ({
			from: dayjs().subtract(30, "day").startOf("day").toISOString(),
			to: dayjs().endOf("day").toISOString(),
		}),
	},
	{
		label: "Last month",
		getRange: () => ({
			from: dayjs().subtract(1, "month").startOf("month").toISOString(),
			to: dayjs().subtract(1, "month").endOf("month").toISOString(),
		}),
	},
	{
		label: "YTD",
		getRange: () => ({
			from: dayjs().startOf("year").toISOString(),
			to: dayjs().endOf("day").toISOString(),
		}),
	},
	{
		label: "Last year",
		getRange: () => ({
			from: dayjs().subtract(1, "year").startOf("year").toISOString(),
			to: dayjs().subtract(1, "year").endOf("year").toISOString(),
		}),
	},
];

function getDefaultFilters(existingSearchFilter?: ComplexFilterItem): ComplexFilterItem[] {
	const today = dayjs();
	const thirtyDaysAgo = today.subtract(30, "day");
	const oneYear = today.add(365, "day");

	return [
		...(existingSearchFilter ? [existingSearchFilter] : []),
		{
			type: ComplexFilterType.STATUS,
			value: null,
		} as ReservationsStatusComplexFilter,
		{
			type: ComplexFilterType.CHANNEL,
			value: null,
		} as ReservationsChannelComplexFilter,
		{
			type: ComplexFilterType.DATE,
			value: {
				dateFrom: thirtyDaysAgo.startOf("day").toISOString(),
				dateTo: oneYear.endOf("day").toISOString(),
				dateFromType: DateFilterType.CHECKOUT_TIME,
				dateToType: DateFilterType.CHECKOUT_TIME,
			} as DateRangeFilter,
		} as DateComplexFilter,
		{
			type: ComplexFilterType.PROPERTY_ID,
			value: null,
		} as PropertyIdComplexFilter,
	];
}

interface FiltersPanelProps {
	isMobile: boolean;
}

const FiltersPanel = ({ isMobile }: FiltersPanelProps): JSX.Element => {
	const filters = useSelector(getReservationsFilter);
	const dispatch = useDispatch();

	const propertiesList = useSelector(getPropertiesList);
	const properties = useMemo(
		() => propertiesList.filter((p) => p.id).map((p) => ({ id: p.id, label: p.name })),
		[propertiesList],
	);
	const [dateApplyTo, setDateApplyTo] = useState<"checkin" | "checkout">("checkin");
	const [activePreset, setActivePreset] = useState<string | null>(null);
	const [filtersOpen, setFiltersOpen] = useState(false);

	useLayoutEffect(() => {
		dispatch(setReservationsFilter(getDefaultFilters()));
	}, [dispatch]);

	const statusFilter = filters.find(
		(f: ComplexFilterItem) => f.type === ComplexFilterType.STATUS,
	) as ReservationsStatusComplexFilter | undefined;

	const channelFilter = filters.find(
		(f: ComplexFilterItem) => f.type === ComplexFilterType.CHANNEL,
	) as ReservationsChannelComplexFilter | undefined;

	const dateFilter = filters.find(
		(f: ComplexFilterItem) => f.type === ComplexFilterType.DATE,
	) as DateComplexFilter | undefined;

	const propertyFilter = filters.find(
		(f: ComplexFilterItem) => f.type === ComplexFilterType.PROPERTY_ID,
	) as PropertyIdComplexFilter | undefined;

	const searchFilter = filters.find(
		(f: ComplexFilterItem) => f.type === ComplexFilterType.SEARCH,
	) as SearchComplexFilter | undefined;

	const selectedStatus = statusFilter?.value || "all";
	const channelSelected = channelFilter?.value ? [channelFilter.value as string] : ["all"];
	const selectedPropertyId = propertyFilter?.value || null;

	const dateRange: DateRange = {
		from: dateFilter?.value?.dateFrom ? new Date(dateFilter.value.dateFrom) : undefined,
		to: dateFilter?.value?.dateTo ? new Date(dateFilter.value.dateTo) : undefined,
	};

	const updateFilter = useCallback(
		(updatedFilter: ComplexFilterItem) => {
			const exists = filters.some((f: ComplexFilterItem) => f.type === updatedFilter.type);
			const updatedFilters = exists
				? filters.map((f: ComplexFilterItem) =>
						f.type === updatedFilter.type ? updatedFilter : f,
					)
				: [...filters, updatedFilter];
			dispatch(setReservationsFilter(updatedFilters));
			dispatch(getReservations());
		},
		[filters, dispatch],
	);

	const handleDateChange = (range: DateRange) => {
		if (!dateFilter) return;
		setActivePreset(null);
		const updatedFilter: DateComplexFilter = {
			type: ComplexFilterType.DATE,
			value: {
				dateFrom: range.from ? range.from.toISOString() : dateFilter.value?.dateFrom ?? null,
				dateTo: range.to ? range.to.toISOString() : null,
				dateFromType: DateFilterType.CHECKOUT_TIME,
				dateToType: DateFilterType.CHECKOUT_TIME,
			} as DateRangeFilter,
		};
		const updatedFilters = filters.map((f: ComplexFilterItem) =>
			f.type === updatedFilter.type ? updatedFilter : f,
		);
		dispatch(setReservationsFilter(updatedFilters));
		if (range.from && range.to) {
			dispatch(getReservations());
		}
	};

	const handleStatusChange = (value: string) => {
		updateFilter({
			type: ComplexFilterType.STATUS,
			value: value === "all" ? null : (value as RESERVATION_STATUSES),
		} as ReservationsStatusComplexFilter);
	};

	const handleChannelChange = (items: string[]) => {
		if (items.includes("all") || items.length === 0) {
			updateFilter({
				type: ComplexFilterType.CHANNEL,
				value: null,
			} as ReservationsChannelComplexFilter);
		} else {
			updateFilter({
				type: ComplexFilterType.CHANNEL,
				value: items[0] as CHANNEL_NAMES,
			} as ReservationsChannelComplexFilter);
		}
	};

	const handlePropertyChange = (value: string | null) => {
		updateFilter({
			type: ComplexFilterType.PROPERTY_ID,
			value: value || null,
		} as PropertyIdComplexFilter);
	};

	const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
		updateFilter({
			type: ComplexFilterType.SEARCH,
			value: e.target.value || null,
		} as SearchComplexFilter);
	};

	const handleReset = () => {
		setActivePreset(null);
		const existingSearch = filters.find(
			(f: ComplexFilterItem) => f.type === ComplexFilterType.SEARCH,
		);
		const resetSearch = existingSearch
			? { ...existingSearch, value: null } as SearchComplexFilter
			: undefined;
		dispatch(setReservationsFilter(getDefaultFilters(resetSearch)));
		dispatch(getReservations());
	};

	const handlePresetClick = useCallback(
		(preset: (typeof DATE_PRESETS)[number]) => {
			setActivePreset(preset.label);
			const range = preset.getRange();
			updateFilter({
				type: ComplexFilterType.DATE,
				value: {
					dateFrom: range.from,
					dateTo: range.to,
					dateFromType: DateFilterType.CHECKOUT_TIME,
					dateToType: DateFilterType.CHECKOUT_TIME,
				} as DateRangeFilter,
			} as DateComplexFilter);
		},
		[updateFilter],
	);

	const activeFilterCount =
		(channelSelected[0] !== "all" ? 1 : 0) +
		(selectedPropertyId ? 1 : 0) +
		(selectedStatus !== "all" && selectedStatus !== RESERVATION_STATUSES.ACCEPTED ? 1 : 0);

	if (isMobile) {
		return (
			<div className="flex flex-col gap-2 p-4 bg-card rounded-lg border border-border relative z-10 mb-2">
				{/* Search */}
				<div className="relative">
					<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
					<Input
						placeholder="Search reservations..."
						value={searchFilter?.value || ""}
						onChange={handleSearchChange}
						aria-label="Search reservations"
						className="pl-9"
					/>
				</div>

				{/* Date Presets */}
				<div className="flex flex-wrap gap-1.5">
					{DATE_PRESETS.map((preset) => (
						<Badge
							key={preset.label}
							variant={activePreset === preset.label ? "secondary" : "outline"}
							className="cursor-pointer hover:bg-accent transition-colors px-2 py-0.5 text-xs"
							onClick={() => handlePresetClick(preset)}
						>
							{preset.label}
						</Badge>
					))}
				</div>

				{/* Date Range + Filter Toggle */}
				<div className="flex items-center gap-2">
					<DateRangePicker
						value={dateRange}
						onChange={handleDateChange}
						numberOfMonths={1}
						className="flex-1 w-auto text-xs h-8"
					/>
					<Button
						variant={filtersOpen ? "secondary" : "outline"}
						size="sm"
						className="h-8 px-2.5 shrink-0"
						onClick={() => setFiltersOpen(!filtersOpen)}
					>
						<SlidersHorizontal className="h-3.5 w-3.5" />
						{activeFilterCount > 0 && (
							<span className="ml-1 text-[10px] bg-primary text-primary-foreground rounded-full h-4 w-4 flex items-center justify-center">
								{activeFilterCount}
							</span>
						)}
					</Button>
				</div>

				{/* Expandable filters */}
				{filtersOpen && (
					<div className="flex flex-col gap-2">
						<div className="flex gap-2">
							<MultiSelect
								items={CHANNEL_OPTIONS}
								selectedItems={channelSelected}
								onSelectionChange={handleChannelChange}
								placeholder="Channel"
								className="flex-1"
								renderSelectedTags={false}
							/>
							<div className="flex-1">
								<SearchableSelect
									items={properties}
									value={selectedPropertyId}
									onChange={handlePropertyChange}
									placeholder="All Properties"
									searchPlaceholder="Search properties..."
									emptyMessage="No property found."
								/>
							</div>
						</div>
						<div className="flex gap-2">
							<div className="relative flex-1">
								<Select value={selectedStatus} onValueChange={handleStatusChange}>
									<SelectTrigger className="w-full">
										<SelectValue placeholder="All" />
									</SelectTrigger>
									<SelectContent>
										{STATUS_OPTIONS.map((option) => (
											<SelectItem key={option.id} value={option.id}>
												{option.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
							<Select
								value={dateApplyTo}
								onValueChange={(v) => setDateApplyTo(v as "checkin" | "checkout")}
							>
								<SelectTrigger className="flex-1">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="checkin">Check-in</SelectItem>
									<SelectItem value="checkout">Check-out</SelectItem>
								</SelectContent>
							</Select>
							<Button variant="ghost" size="sm" onClick={handleReset}>
								Reset
							</Button>
						</div>
					</div>
				)}
			</div>
		);
	}

	// --- DESKTOP LAYOUT ---
	return (
		<div className="flex flex-col gap-2 p-4 bg-card rounded-lg border border-border relative z-10 mb-2">
			{/* Search */}
			<div className="relative sm:w-80">
				<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
				<Input
					placeholder="Search reservations..."
					value={searchFilter?.value || ""}
					onChange={handleSearchChange}
					aria-label="Search reservations"
					className="pl-9"
				/>
			</div>

			{/* Date Presets */}
			<div className="flex flex-wrap gap-1.5">
				{DATE_PRESETS.map((preset) => (
					<Badge
						key={preset.label}
						variant={activePreset === preset.label ? "secondary" : "outline"}
						className="cursor-pointer hover:bg-accent transition-colors px-2 py-0.5 text-xs"
						onClick={() => handlePresetClick(preset)}
					>
						{preset.label}
					</Badge>
				))}
			</div>

			{/* Filters */}
			<div className="flex flex-wrap items-start gap-4">
				{/* Date Range */}
				<div className="space-y-1">
					<span className="text-xs font-medium text-muted-foreground">Date range</span>
					<div>
						<DateRangePicker
							value={dateRange}
							onChange={handleDateChange}
							numberOfMonths={2}
						/>
					</div>
				</div>

				{/* Apply To */}
				<div className="space-y-1">
					<span className="text-xs font-medium text-muted-foreground">Apply to</span>
					<Select
						value={dateApplyTo}
						onValueChange={(v) => setDateApplyTo(v as "checkin" | "checkout")}
					>
						<SelectTrigger className="w-[120px] hover:bg-accent hover:text-accent-foreground transition-colors">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="checkin">Check-in</SelectItem>
							<SelectItem value="checkout">Check-out</SelectItem>
						</SelectContent>
					</Select>
				</div>

				{/* Channel */}
				<div className="space-y-1">
					<span className="text-xs font-medium text-muted-foreground">Channel</span>
					<MultiSelect
						items={CHANNEL_OPTIONS}
						selectedItems={channelSelected}
						onSelectionChange={handleChannelChange}
						placeholder="All Channels"
						className="w-[180px]"
						renderSelectedTags={false}
					/>
					{channelSelected[0] !== "all" && (
						<div className="flex flex-wrap gap-1 mt-1 w-[180px]">
							{channelSelected.filter((id) => id !== "all").map((id) => {
								const label = CHANNEL_OPTIONS.find((c) => c.id === id)?.label || id;
								return (
									<Badge key={id} variant="secondary" className="text-xs">
										{label}
										<button type="button" aria-label={`Remove ${label}`} className="ml-1 leading-none hover:opacity-80" onClick={() => { const next = channelSelected.filter((s) => s !== id); handleChannelChange(next.length === 0 ? ["all"] : next); }}>&times;</button>
									</Badge>
								);
							})}
						</div>
					)}
				</div>

				{/* Property */}
				<div className="space-y-1">
					<span className="text-xs font-medium text-muted-foreground">Property</span>
					<div className="w-[200px]">
						<SearchableSelect
							items={properties}
							value={selectedPropertyId}
							onChange={handlePropertyChange}
							placeholder="All Properties"
							searchPlaceholder="Search properties..."
							emptyMessage="No property found."
						/>
					</div>
					{selectedPropertyId && (
						<div className="flex flex-wrap gap-1 mt-1 w-[200px]">
							{(() => {
								const label = properties.find((p) => p.id === selectedPropertyId)?.label || selectedPropertyId;
								return (
									<Badge variant="secondary" className="text-xs">
										{label}
										<button type="button" aria-label={`Remove ${label}`} className="ml-1 leading-none hover:opacity-80" onClick={() => handlePropertyChange(null)}>&times;</button>
									</Badge>
								);
							})()}
						</div>
					)}
				</div>

				{/* Status */}
				<div className="space-y-1">
					<span className="text-xs font-medium text-muted-foreground">Status</span>
					<div className="w-[150px]">
						<Select value={selectedStatus} onValueChange={handleStatusChange}>
							<SelectTrigger className="w-full hover:bg-accent hover:text-accent-foreground transition-colors">
								<SelectValue placeholder="All" />
							</SelectTrigger>
							<SelectContent>
								{STATUS_OPTIONS.map((option) => (
									<SelectItem key={option.id} value={option.id}>
										{option.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
					{selectedStatus !== "all" && (
						<div className="flex flex-wrap gap-1 mt-1 w-[150px]">
							{(() => {
								const label = STATUS_OPTIONS.find((o) => o.id === selectedStatus)?.label || selectedStatus;
								return (
									<Badge variant="secondary" className="text-xs">
										{label}
										<button type="button" aria-label={`Remove ${label}`} className="ml-1 leading-none hover:opacity-80" onClick={() => handleStatusChange("all")}>&times;</button>
									</Badge>
								);
							})()}
						</div>
					)}
				</div>

				{/* Reset Button */}
				<div className="space-y-1">
					<span className="text-xs font-medium text-muted-foreground">&nbsp;</span>
					<div className="flex items-center h-9">
						<Button variant="ghost" size="sm" onClick={handleReset}>
							Reset
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default FiltersPanel;
